import React, { useState, useEffect, useCallback } from 'react';
import {
  Segment, Header, Icon, Form, Button, Message, Divider
} from 'semantic-ui-react';
import FileDropZone from './FileDropZone';
import { toKebab } from './FileStagingList';

/**
 * BookEntryForm — Per-book sub-wizard within the Prototype Builder.
 *
 * Flow: Enter Book Name → Upload PDF → Upload Cover → Save Book
 * 
 * The book name drives the default alias for both the PDF and cover files.
 * Users can override the alias, but by default:
 *   - PDF alias: {book-name-kebab}.pdf
 *   - Cover alias: {book-name-kebab}.{original-ext}
 *
 * Props:
 *   onSaveBook    — callback({ bookTitle, pdfFile, pdfAlias, coverFile, coverAlias })
 *   editingBook   — existing book data to populate for editing (or null for new)
 *   onCancelEdit  — callback to cancel editing an existing book
 *   disabled      — boolean (locked prototype)
 *   saving        — boolean (loading state)
 */
const BookEntryForm = ({ onSaveBook, editingBook = null, onCancelEdit, disabled = false, saving = false }) => {
  // Book name
  const [bookTitle, setBookTitle] = useState('');

  // PDF staging (single file)
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfAlias, setPdfAlias] = useState('');
  const [pdfAliasOverridden, setPdfAliasOverridden] = useState(false);

  // Cover staging (single file)
  const [coverFile, setCoverFile] = useState(null);
  const [coverAlias, setCoverAlias] = useState('');
  const [coverAliasOverridden, setCoverAliasOverridden] = useState(false);

  // Messages
  const [message, setMessage] = useState(null);

  // Populate form when editing an existing book
  useEffect(() => {
    if (editingBook) {
      setBookTitle(editingBook.bookTitle || '');
      setPdfAlias(editingBook.pdfAlias || editingBook.fileName || '');
      setCoverAlias(editingBook.coverAlias || editingBook.coverFileName || '');
      // Files can't be re-populated (already uploaded) — show as "existing"
      setPdfFile(null);
      setCoverFile(null);
      setPdfAliasOverridden(true);
      setCoverAliasOverridden(!!editingBook.coverFileName);
    }
  }, [editingBook]);

  // Auto-update PDF alias from book title (unless user overrode it)
  useEffect(() => {
    if (bookTitle && !pdfAliasOverridden) {
      setPdfAlias(toKebab(bookTitle) + '.pdf');
    }
  }, [bookTitle, pdfAliasOverridden]);

  // Auto-update cover alias from book title (unless user overrode it)
  useEffect(() => {
    if (bookTitle && !coverAliasOverridden && coverFile) {
      const ext = '.' + coverFile.name.split('.').pop();
      setCoverAlias(toKebab(bookTitle) + ext);
    }
  }, [bookTitle, coverAliasOverridden, coverFile]);

  const handlePdfDrop = useCallback((files) => {
    const file = files[0];
    setPdfFile(file);
    if (!pdfAliasOverridden && !bookTitle) {
      // No book title yet — default alias to original filename
      setPdfAlias(file.name);
    }
    // If book title exists, the useEffect above already set the alias
  }, [pdfAliasOverridden, bookTitle]);

  const handleCoverDrop = useCallback((files) => {
    const file = files[0];
    setCoverFile(file);
    if (!coverAliasOverridden && !bookTitle) {
      setCoverAlias(file.name);
    }
  }, [coverAliasOverridden, bookTitle]);

  const handlePdfAliasChange = (val) => {
    setPdfAlias(val);
    setPdfAliasOverridden(true);
  };

  const handleCoverAliasChange = (val) => {
    setCoverAlias(val);
    setCoverAliasOverridden(true);
  };

  const resetPdfAliasToDefault = () => {
    setPdfAliasOverridden(false);
    if (bookTitle) {
      setPdfAlias(toKebab(bookTitle) + '.pdf');
    } else if (pdfFile) {
      setPdfAlias(pdfFile.name);
    }
  };

  const resetCoverAliasToDefault = () => {
    setCoverAliasOverridden(false);
    if (bookTitle && coverFile) {
      const ext = '.' + coverFile.name.split('.').pop();
      setCoverAlias(toKebab(bookTitle) + ext);
    } else if (coverFile) {
      setCoverAlias(coverFile.name);
    }
  };

  const handleSave = () => {
    if (!bookTitle.trim()) {
      setMessage({ type: 'error', text: 'Book title is required.' });
      return;
    }
    if (!pdfFile && !editingBook?.fileName) {
      setMessage({ type: 'error', text: 'Please upload a PDF file for this book.' });
      return;
    }

    onSaveBook({
      bookTitle: bookTitle.trim(),
      pdfFile,
      pdfAlias: pdfAlias || (pdfFile ? pdfFile.name : ''),
      coverFile,
      coverAlias: coverAlias || (coverFile ? coverFile.name : ''),
      // Pass editing context if editing
      editingUuid: editingBook?.uuid || null,
    });

    // Clear form for next book
    clearForm();
    setMessage({ type: 'success', text: `"${bookTitle.trim()}" saved! Add another book below or click "Done Adding Books."` });
    setTimeout(() => setMessage(null), 5000);
  };

  const clearForm = () => {
    setBookTitle('');
    setPdfFile(null);
    setPdfAlias('');
    setPdfAliasOverridden(false);
    setCoverFile(null);
    setCoverAlias('');
    setCoverAliasOverridden(false);
  };

  const handleCancel = () => {
    clearForm();
    setMessage(null);
    if (onCancelEdit) onCancelEdit();
  };

  const isEditing = !!editingBook;

  return (
    <Segment>
      <Header as="h3">
        <Icon name={isEditing ? 'edit' : 'book'} />
        <Header.Content>
          {isEditing ? `Edit: ${editingBook.bookTitle}` : 'Add a Book'}
          <Header.Subheader>
            {isEditing
              ? 'Update this book\'s details and files'
              : 'Enter the book title first — it sets the default file names'}
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Form>
        {/* Step 2a: Book Title */}
        <Form.Input
          label="Book Title"
          placeholder="Enter the book title (e.g., The Dark Forest)..."
          value={bookTitle}
          onChange={(e) => setBookTitle(e.target.value)}
          required
          disabled={disabled}
          icon="book"
          iconPosition="left"
          size="large"
        />

        {bookTitle && (
          <Message info size="mini" style={{ marginTop: '-0.5em', marginBottom: '1em' }}>
            <Icon name="magic" />
            PDF will be named: <strong>{pdfAlias || toKebab(bookTitle) + '.pdf'}</strong>
            {coverFile && (
              <span> | Cover: <strong>{coverAlias}</strong></span>
            )}
          </Message>
        )}

        <Divider />

        {/* Step 2b: Upload PDF */}
        <Header as="h4" style={{ marginTop: '0.5em' }}>
          <Icon name="file pdf outline" color="red" />
          PDF File
        </Header>

        {pdfFile ? (
          <Segment secondary>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
              <Icon name="file pdf outline" color="red" size="large" />
              <div style={{ flex: 1 }}>
                <Form.Input
                  label="PDF File Name (alias)"
                  value={pdfAlias}
                  onChange={(e) => handlePdfAliasChange(e.target.value)}
                  disabled={disabled}
                  size="small"
                  action={pdfAliasOverridden ? {
                    icon: 'undo',
                    title: 'Reset to default name',
                    onClick: resetPdfAliasToDefault,
                  } : undefined}
                />
                <div style={{ fontSize: '0.8em', color: '#888' }}>
                  Original: {pdfFile.name} ({(pdfFile.size / (1024 * 1024)).toFixed(1)} MB)
                </div>
              </div>
              <Button
                icon="close"
                basic
                color="red"
                size="mini"
                onClick={() => { setPdfFile(null); setPdfAlias(''); setPdfAliasOverridden(false); }}
                title="Remove PDF"
                disabled={disabled}
              />
            </div>
          </Segment>
        ) : editingBook?.fileName ? (
          <Segment secondary>
            <Icon name="check circle" color="green" />
            Existing PDF: <strong>{editingBook.fileName}</strong>
          </Segment>
        ) : (
          <FileDropZone
            accept=".pdf"
            multiple={false}
            label="Drop PDF file here"
            sublabel={bookTitle ? `Will be saved as: ${toKebab(bookTitle)}.pdf` : 'Enter a book title above first'}
            icon="file pdf outline"
            disabled={disabled || !bookTitle}
            maxSizeMB={100}
            onFilesSelected={handlePdfDrop}
          />
        )}

        <Divider />

        {/* Step 2c: Upload Cover */}
        <Header as="h4" style={{ marginTop: '0.5em' }}>
          <Icon name="image" color="blue" />
          Cover Image <span style={{ fontSize: '0.7em', color: '#999' }}>(optional)</span>
        </Header>

        {coverFile ? (
          <Segment secondary>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
              <Icon name="image" color="blue" size="large" />
              <div style={{ flex: 1 }}>
                <Form.Input
                  label="Cover File Name (alias)"
                  value={coverAlias}
                  onChange={(e) => handleCoverAliasChange(e.target.value)}
                  disabled={disabled}
                  size="small"
                  action={coverAliasOverridden ? {
                    icon: 'undo',
                    title: 'Reset to default name',
                    onClick: resetCoverAliasToDefault,
                  } : undefined}
                />
                <div style={{ fontSize: '0.8em', color: '#888' }}>
                  Original: {coverFile.name} ({(coverFile.size / (1024 * 1024)).toFixed(1)} MB)
                </div>
              </div>
              <Button
                icon="close"
                basic
                color="red"
                size="mini"
                onClick={() => { setCoverFile(null); setCoverAlias(''); setCoverAliasOverridden(false); }}
                title="Remove cover"
                disabled={disabled}
              />
            </div>
          </Segment>
        ) : editingBook?.coverFileName ? (
          <Segment secondary>
            <Icon name="check circle" color="green" />
            Existing cover: <strong>{editingBook.coverFileName}</strong>
          </Segment>
        ) : (
          <FileDropZone
            accept=".jpg,.jpeg,.png,.webp"
            multiple={false}
            label="Drop cover image here"
            sublabel={bookTitle ? `Will be saved as: ${toKebab(bookTitle)}.jpg` : 'Enter a book title above first'}
            icon="image"
            disabled={disabled}
            maxSizeMB={20}
            onFilesSelected={handleCoverDrop}
          />
        )}
      </Form>

      {message && (
        <Message
          positive={message.type === 'success'}
          negative={message.type === 'error'}
          size="small"
          style={{ marginTop: '1em' }}
        >
          <Icon name={message.type === 'success' ? 'check' : 'warning sign'} />
          {message.text}
        </Message>
      )}

      <div style={{ display: 'flex', gap: '0.5em', justifyContent: 'flex-end', marginTop: '1em' }}>
        {(isEditing || bookTitle || pdfFile || coverFile) && (
          <Button onClick={handleCancel} disabled={saving}>
            {isEditing ? 'Cancel Edit' : 'Clear'}
          </Button>
        )}
        <Button
          primary
          onClick={handleSave}
          disabled={disabled || saving || !bookTitle.trim()}
          loading={saving}
        >
          <Icon name={isEditing ? 'save' : 'plus'} />
          {isEditing ? 'Update Book' : 'Save Book'}
        </Button>
      </div>
    </Segment>
  );
};

export default BookEntryForm;
